import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild} from '@angular/core';
import {CommonModule, Location} from '@angular/common';
import {
  AdditionalPACData,
  PacUpdateRequest,
  PaidPaymentCollection,
  PaymentCategory,
  PaymentFrequency
} from '../../models/additional-pac-data.model';
import {catchError, EMPTY, Observable, of, switchMap, tap} from 'rxjs';
import {PaymentMethodUtils} from '../../shared/utils/payment-mode.utils';
import {PayNowComponent} from '../../components/pay-now/pay-now.component';
import {CreditDebitCardComponent} from '../../components/credit-debit-card/credit-debit-card.component';
import {ChequeDepositComponent} from '../../components/cheque-deposit/cheque-deposit.component';
import {finalize} from 'rxjs/operators';
import {ConfigurationService} from '../../shared/services/configuration.service';
import {PaymentMethods, PaymentMode} from '../../models/payment-method.model';
import {ActivatedRoute, Router} from '@angular/router';
import {PacService} from '../../shared/services/pac.service';
import {PaymentBlockService} from '../../services/payment-block.service';
import {CustomModalComponent} from '../custom-modal/custom-modal.component';
import {CourtDepositService} from '../../shared/services/court-deposit.service';
import {PaymentDetailsService} from '../../shared/services/payment-details.service';
import {ErrorBannerComponent} from '../error-banner/error-banner.component';
import {AppConstants} from '../../shared/app-constants';
import {SessionEventService} from '../../shared/services/session-event-service';


@Component({
  selector: 'app-payment-method',
  imports: [
    CommonModule,
    PayNowComponent,
    CreditDebitCardComponent,
    ChequeDepositComponent,
    CustomModalComponent,
    ErrorBannerComponent
  ],
  templateUrl: './payment-method.component.html',
  styleUrl: './payment-method.component.scss',
  standalone: true
})
export class PaymentMethodComponent implements OnChanges, OnInit {

  @Input() pacDetails!: AdditionalPACData;
  selectedPaymentMethod: string | undefined | null = null;
  paymentMethods: PaymentMethods[] = [];
  paymentModeError: string = '';
  @ViewChild('paymentConfirmationModalRef') paymentConfirmationModalRef!: CustomModalComponent;
  @ViewChild('receiptConfirmationModalRef') receiptConfirmationModalRef!: CustomModalComponent;
  isPaymentCompleted: boolean = false;
  paymentConfirmationMessage: string = "";
  receiptConfirmationMessage: string = "";
  errorMessage: string | null = null;
  @Input() isAborted: boolean = false;
  @Input() isPayNowTransactionMissing: boolean = false;
  payNowAttemptedCount: number = 0;
  @Input() selectedPacCount!: number;

  @Output() pacDetailsUpdated = new EventEmitter<AdditionalPACData>();
  @Output() transactionUpdated = new EventEmitter<PaidPaymentCollection>();
  @Output() arePaymentMethodsAvailable = new EventEmitter<boolean>();
  private selectedPacNumbers: string[] = [];
  private pacAmountsMap: { [key: string]: number } = {};
  // Set once /deposit-payment succeeds - no further deposit call is allowed for this PAC
  private isDepositCreated: boolean = false;
  private isDepositInProgress: boolean = false;
  private hasRedirectedAfterDeposit: boolean = false;

  constructor(private readonly router: Router,
              private readonly configService: ConfigurationService,
              private readonly pacService: PacService,
              private readonly courtDepositService: CourtDepositService,
              private readonly paymentDetailsService: PaymentDetailsService,
              private readonly route: ActivatedRoute,
              private readonly backLocation: Location,
              private readonly paymentBlockService: PaymentBlockService,
              private readonly sessionEventService: SessionEventService) {
  }

  private handleBlockedResponse(err: any): boolean {
    if (err?.status === 409 && err?.error?.blocked) {
      this.paymentBlockService.notifyBlocked(this.pacDetails.pacNumber, err.error.retryAfterSeconds);
      return true;
    }
    return false;
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.route.queryParams.subscribe(params => {
      const state = history.state;
      this.selectedPacNumbers = state?.selectedPacNumbers || state?.pacDetails?.selectedPacNumbers || [];
      this.pacAmountsMap = state?.pacAmountsMap || state?.pacDetails?.pacAmountsMap || {} ;
      this.fetchApplicablePaymentModes();
    });
  }

  ngOnInit(): void {
    this.fetchApplicablePaymentModes();
  }

  onSelectMethod(paymentMethod: string | null) {
    this.selectedPaymentMethod =
      this.selectedPaymentMethod === paymentMethod ? null : paymentMethod;
  }

  fetchApplicablePaymentModes(): void {
    this.configService.fetchPaymentModes(this.pacDetails.pacNumber ??'').pipe(
      tap(response => {
        this.paymentMethods = PaymentMethodUtils.enrichPaymentMethods(response);
        const available = this.hasValidPaymentMethods();
        this.arePaymentMethodsAvailable.emit(available);
      }),
      catchError(err => { // NOSONAR
        this.paymentModeError = "Error encountered." +
          "- If you are within court premise, please approach our cashier counter for assistance" +
          "- If you are not on court premises, please contact the court officer or email a screenshot of the error encountered to \"contact@judiciary.gov.sg\" or call 1800 587 8423 for further assistance.";
        this.arePaymentMethodsAvailable.emit(false);
        return EMPTY;
      }),
      finalize(() => {
      })
    ).subscribe();
  }

  hasValidPaymentMethods(): boolean {
    return this.paymentMethods?.some(pm =>
      ['PAY_NOW', 'CHEQUE', 'CREDIT_CARD'].includes(pm.paymentMode)
    ) ?? false;
  }

  onGenerateReceipt() {
    this.receiptConfirmationMessage = "If you proceed to generate receipt, you will exit the payment page and will not be able to make payment for the balance amount. Do you want to continue?";
    this.receiptConfirmationModalRef.showModal();
  }

  handleReceiptModalClose(confirmed: boolean) {
    if (!confirmed || !this.pacDetails.paidLineItems) {
      return;
    }
    if (this.pacDetails.paymentCategory === PaymentCategory.COURT_DEPOSIT) {
      this.createDepositDetails(true);
      return;
    }
    this.payNowAttemptedCheck();
    if (this.pacDetails.pacNumber) {
      this.pacService.fetchAdditionalInfo(this.pacDetails.pacNumber).subscribe({
        next: (updatedPacDetails: AdditionalPACData) => {
          this.router.navigate(['payment-success'], {
            state: {
              pacDetails: updatedPacDetails,
              isPayNowTransactionMissing: this.isPayNowTransactionMissing,
              payNowAttemptedCount: this.payNowAttemptedCount
            }
          });
        },
        error: (error) => {
        }
      });
    }
  }

  partialPaymentAbortCheck() {
    const totalAmount = this.pacDetails.totalAmountPayable;
    const balanceAmount = this.pacDetails.balancePayable;
    if (this.pacDetails.paymentFrequency === PaymentFrequency.O) {
      if (totalAmount != balanceAmount && balanceAmount != 0) {
        this.isAborted = true;
      }
    } else {
      const remaining = this.pacDetails.selectedAmountPayable && (this.pacDetails.selectedAmountPayable - this.pacDetails.amountPaid);
      if (remaining && (remaining > 0 && remaining !== this.pacDetails.selectedAmountPayable)) {
        this.isAborted = true;
      }
    }
  }

  payNowAttemptedCheck() {
    const attemptKey = 'PAY_NOW_ATTEMPTED';
    this.payNowAttemptedCount = Number(localStorage.getItem(attemptKey)) || 0;
    const payNowTransactionsFoundCount = this.pacDetails.payNowCompletedCount;
    if (payNowTransactionsFoundCount != undefined && payNowTransactionsFoundCount < this.payNowAttemptedCount) {
      this.isPayNowTransactionMissing = true;
    }
  }

  handleConfirmationModalClose(confirmed: boolean) {
    this.paymentConfirmationModalRef.hideModal();
    if (this.isDepositCreated) {
      this.hasRedirectedAfterDeposit = true;
    }
    if (this.isPaymentCompleted) {
      this.redirectToPaymentSuccessOnFullPayment();
    } else {
      this.selectedPaymentMethod = null;
    }
    this.handleUpdatePacDetails();
    this.pacDetailsUpdated.emit(this.pacDetails);

  }

  // The confirmation modal can also be closed by its header X or the idle timeout's forced close,
  // neither of which emits onCloseModel - once the deposit exists, still take the user to payment-success
  onConfirmationModalHidden() {
    if (this.isDepositCreated && !this.hasRedirectedAfterDeposit) {
      this.handleConfirmationModalClose(true);
    }
  }

  showConfirmationModal() {
    this.paymentConfirmationMessage = "Cheque/Cashier's Order details recorded successfully. Payment will be rendered void if the cheque is not honoured by the bank due to any reason.";
    this.paymentConfirmationModalRef.showModal();
  }

  updatePaymentCollectionToPac(requestBody: PaidPaymentCollection) {
    this.updatePaymentCollections(requestBody).subscribe();

  }

  updatePaymentCollections(requestBody: PaidPaymentCollection): Observable<any> {
    return  this.pacService.addPaymentCollectionToPac(requestBody).pipe(
      tap((response) => {
        this.pacDetails = response;
      }),
      switchMap(() => {
        if (this.pacDetails.paymentCategory === PaymentCategory.COURT_DEPOSIT) {
          this.showConfirmationModal();
          return of(null);
        } else {
          return this.updatePacDetails();
        }
      }),
      catchError((err) => {
        // Optionally show a toast/snackbar error message here
        return of(null); // or throwError if you want the error to propagate
      }),
      finalize(() => {
        // Optional: hide loading spinner or do clean-up
      })
    );
  }

  createDepositDetails(generateReceipt: boolean = false, newLineItem?: PaidPaymentCollection) {
    if (this.isDepositInProgress) {
      return;
    }
    if (this.isDepositCreated) {
      // Deposit already exists (e.g. an earlier receipt attempt failed after it) - only finish the receipt step
      if (generateReceipt) {
        this.updateDepositPacInfo();
      }
      return;
    }
    // Send a copy - pacDetails is only updated once the deposit succeeds, so a failed call can be retried without duplicating the line
    const depositRequest = this.buildDepositRequest(newLineItem);
    this.isDepositInProgress = true;
    this.courtDepositService.saveCourtDeposit(depositRequest).pipe(
      tap(response => {
        this.onDepositCreated(depositRequest);

        if (generateReceipt) {
          this.updateDepositPacInfo();
        } else {
          this.showConfirmationModal();
        }
      }),
      catchError(error => {
        if (!this.handleBlockedResponse(error)) {
          this.errorMessage = "Cheque deposit failed. Please try again or contact support.";
        }
        return EMPTY; // prevent further subscription
      }),
      finalize(() => {
        this.isDepositInProgress = false;
      })
    ).subscribe();
  }

  private updateDepositPacInfo() {
    const pacUpdateRequest: PacUpdateRequest = {
      pacNumber: this.pacDetails.pacNumber,
      emailId: this.pacDetails.emailId || '',
      contactNumber: this.pacDetails.contactNumber || '',
      selectedAmountPayable: this.pacDetails.selectedAmountPayable,
      paymentFrequency: this.pacDetails.paymentFrequency ?? PaymentFrequency.O,
      paymentCategory: this.pacDetails.paymentCategory ?? PaymentCategory.COURT_DEPOSIT
    };
    this.pacService.updateAdditionalInfo(pacUpdateRequest).subscribe({
      next: (updatedPacDetails: AdditionalPACData) => {
        this.pacDetails = updatedPacDetails;
        this.redirectToPaymentSuccessOnFullPayment();
      },
      error: (err) => {
        const errorMsg = err?.error?.error.message || err?.message;
        this.handleConnectionRefused(errorMsg);
        this.paymentModeError = AppConstants.DEPOSIT_PAYMENT_FAIL_ERROR_MESSAGE;
      }
    });
  }

  private buildDepositRequest(newLineItem?: PaidPaymentCollection): AdditionalPACData {
    const paidLineItems = [...(this.pacDetails.paidLineItems ?? [])];
    if (newLineItem) {
      paidLineItems.push(newLineItem);
    }
    const amountPaid = this.roundToCents(paidLineItems.reduce((sum, item) => sum + (item.paymentAmount ?? 0), 0));
    const balancePayable = Math.max(0, this.roundToCents((this.pacDetails.totalAmountPayable ?? 0) - amountPaid));
    return {...this.pacDetails, paidLineItems, amountPaid, balancePayable};
  }

  private onDepositCreated(depositRequest: AdditionalPACData) {
    this.isDepositCreated = true;
    this.sessionEventService.markDepositCreated(this.pacDetails.pacNumber);
    // Updated in place so the parent page (which shares this object) sees the recorded line too
    this.pacDetails.paidLineItems = depositRequest.paidLineItems;
    this.pacDetails.amountPaid = depositRequest.amountPaid;
    this.pacDetails.balancePayable = depositRequest.balancePayable;
  }

  private roundToCents(amount: number): number {
    return Math.round(amount * 100) / 100;
  }

  onPaymentUpdate(paidPaymentCollection: PaidPaymentCollection) {
    const paidAmountPerRecord = paidPaymentCollection.paymentAmount ?? 0;
    const balancePayable = this.pacDetails?.balancePayable ?? 0;

    // Calculate remaining balance
    const remainingBalance = balancePayable - paidAmountPerRecord;

    // Round to avoid floating-point precision issues
    this.isPaymentCompleted = Math.round(remainingBalance * 100) <= 0;

    if (this.pacDetails.paymentCategory === PaymentCategory.COURT_DEPOSIT) {
      if (this.isDepositCreated) {
        return;
      }
      if (this.isPaymentCompleted) {
        this.createDepositDetails(false, paidPaymentCollection);
      } else {
        this.updatePaymentCollectionToPac(paidPaymentCollection);
      }
    } else if (this.pacDetails.paymentCategory === PaymentCategory.COURT_PAYMENT) {
      const pacNumber = this.pacDetails?.pacNumber;
      // fetch additionalPacInfo here to retrieve the cashCounterID
      if (pacNumber) {
        this.pacService.fetchAdditionalInfo(pacNumber).subscribe({
          next: (response: AdditionalPACData) => {
            this.pacDetails = response;
            this.updateCourtPaymentDetails(paidPaymentCollection)
          },
          error: () => { // NOSONAR
            // intentionally ignored or handled globally
          }
        });
      }
    }

  }

  get hasPaidLineItems(): boolean {
    return !!this.pacDetails?.paidLineItems?.length;
  }
  updateCourtPaymentDetails(paidPaymentCollection: PaidPaymentCollection) {
    const selectedPACs = this.selectedPacNumbers?.length
      ? this.selectedPacNumbers
      : [this.pacDetails.pacNumber];

    const payerId = this.pacDetails?.payerId;
    const kioskCode = this.pacDetails.cashCounterID;
    const eligiblePACs = this.getEligiblePACs(
      selectedPACs,
      paidPaymentCollection.paymentAmount,
      this.pacDetails.amountPaid,
      this.pacAmountsMap
    );

    this.paymentDetailsService
      .submitPayment(paidPaymentCollection, eligiblePACs, payerId, kioskCode)
      .pipe(
        tap((response: any) => {
          const updateInfo = response?.updateCollectionInfoReturn;
          const wsResponse = updateInfo?.wsResponse;
          if (updateInfo?.errorMessage) {
            const errorMsg = AppConstants.PROCESS_PAYMENT_FAIL_ERROR_MESSAGE;
            this.paymentModeError = errorMsg;
          }
          if (wsResponse?.includes('|')) {
            paidPaymentCollection.receiptNumber = wsResponse.split('|')[1];
          }
          const errorMsg = response?.error?.message;
          this.handleConnectionRefused(errorMsg);
        }),
        switchMap(() => this.updatePaymentCollections(paidPaymentCollection)),
        tap(() => {
          this.showConfirmationModal();
        }),
        catchError((err) => {
          if (!this.handleBlockedResponse(err)) {
            const errorMsg = err?.error?.error.message || err?.message;
            this.handleConnectionRefused(errorMsg);
          }
          return of(null);
        }),
        finalize(() => {
        })
      )
      .subscribe();
  }

  private handleConnectionRefused(errorMsg: string): void {
    if (errorMsg?.includes('Connection refused')) {
      this.errorMessage = AppConstants.ERROR_SERVICE_UNAVAILABLE;
      this.paymentModeError = this.errorMessage;
    }
  }

  handleUpdatePacDetails() {
    this.updatePacDetails().subscribe();
  }

  updatePacDetails() : Observable<AdditionalPACData> {
    if (!this.pacDetails.pacNumber) {
      return of(this.pacDetails);
    }
     return this.pacService.fetchAdditionalInfo(this.pacDetails.pacNumber).pipe(
      tap ((response: AdditionalPACData) => {
        this.pacDetails.paidLineItems ??= []; //prefer using nullish coalescing operator ??= instaed of assignment expression
        if (response) {
          const frequency = response.paymentFrequency;
          if (frequency === PaymentFrequency.O) {
            this.pacDetails.totalAmountPayable = response.totalAmountPayable;
            this.pacDetails.balancePayable = response.balancePayable;
            this.pacDetails.amountPaid = response.amountPaid;
            this.pacDetails.pacNumber = response.pacNumber;
            this.pacDetails.payerId = response.payerId;
            this.pacDetails.payerName = response.payerName;
          } else if (response.selectedAmountPayable != null) {  //if statemnet should not be the only sttement in else block
            this.pacDetails.totalAmountPayable = response.selectedAmountPayable;
            this.pacDetails.balancePayable = response.selectedAmountPayable - response.amountPaid;
            this.pacDetails.amountPaid = response.amountPaid;
            this.pacDetails.pacNumber = response.pacNumber;
            this.pacDetails.payerId = response.payerId;
            this.pacDetails.payerName = response.payerName;
          }
          const paidAmountPerSession = response.amountPaid ?? 0;
          const initialTotalAmountPayable = response.totalAmountPayable ?? 0;
          const remainingBalance: number = initialTotalAmountPayable - paidAmountPerSession;
          this.isPaymentCompleted = Math.round(remainingBalance * 100) <= 0;
          if (this.isPaymentCompleted && this.selectedPaymentMethod != PaymentMode.CHEQUE) {
            this.redirectToPaymentSuccessOnFullPayment();
          }
        }
      }
    ));
  }

  redirectToPaymentSuccessOnFullPayment() {
    if (this.pacDetails.paymentCategory === PaymentCategory.COURT_PAYMENT) {
      this.partialPaymentAbortCheck();
    }
    this.payNowAttemptedCheck();
    this.router.navigate(['payment-success'], {
      state: {
        pacDetails: this.pacDetails,
        isAborted: this.isAborted,
        isPayNowTransactionMissing: this.isPayNowTransactionMissing,
        payNowAttemptedCount: this.payNowAttemptedCount
      }
    });
  }

  private getEligiblePACs(
    selectedPACs: (string | undefined)[],
    presentPaymentAmount: number | null,
    totalAmountPaid: number | null,
    pacAmountsMap: { [p: string]: number }
  ): string[] {
    const eligiblePACs: string[] = [];
    let remainingTotal = totalAmountPaid ?? 0; // all payments so far
    let remainingThisPayment = presentPaymentAmount ?? 0; // amount just being paid now

    if (!selectedPACs?.length || !remainingThisPayment) {
      return [];
    }


    for (const pac of selectedPACs) {
      if (!pac) {
        continue;
      }

      const pacAmount = pacAmountsMap[pac] ?? 0;

      // Deduct what was already covered in previous payments
      if (remainingTotal >= pacAmount) {
        remainingTotal -= pacAmount;
        continue; // this PAC is fully paid, skip
      }

      const pacBalance = pacAmount - remainingTotal;
      remainingTotal = 0;

      if (remainingThisPayment > 0) {
        eligiblePACs.push(pac);
        if (remainingThisPayment >= pacBalance) {
          remainingThisPayment -= pacBalance;
        } else {
          remainingThisPayment = 0;
        }
      }

      if (remainingThisPayment <= 0) {
        break;
      }
    }

    return eligiblePACs;
  }

  onBack(): void {
    this.backLocation.back();
  }

}
